import { useEffect, useMemo, useRef, type ReactNode } from "react";

import { Link } from "@tanstack/react-router";
import { Flex, Menu, Tabs, Typography } from "antd";

import subEntityStyles from "../../../styles/SubEntityLayoutSidebar.module.scss";

import {
	analyticsGroups,
	getAnalyticsGroupForReport,
} from "../analyticsConfig";

import styles from "./AnalyticsReportNav.module.scss";

type AnalyticsReportNavProps = {
	activeReportKey: string;
	onReportChange: (reportKey: string) => void;
	children: ReactNode;
};

export const AnalyticsReportNav = ({
	activeReportKey,
	onReportChange,
	children,
}: AnalyticsReportNavProps) => {
	const menuWrapRef = useRef<HTMLDivElement>(null);

	const activeGroup = useMemo(() => {
		const matched = getAnalyticsGroupForReport(activeReportKey);
		return matched ?? analyticsGroups[0];
	}, [activeReportKey]);

	const groupItems = useMemo(
		() =>
			analyticsGroups.map((group) => ({
				key: group.key,
				label: group.label,
			})),
		[]
	);

	const reports = activeGroup?.reports ?? [];

	const menuItems = useMemo(
		() =>
			reports.map((report) => ({
				key: report.key,
				label: (
					<Link to={`/analytics/${report.key}` as "/"}>
						{report.label}
					</Link>
				),
			})),
		[reports]
	);

	useEffect(() => {
		if (!activeReportKey || !reports.some((r) => r.key === activeReportKey)) {
			return;
		}

		const frameID = requestAnimationFrame(() => {
			const selectedItem =
				menuWrapRef.current?.querySelector<HTMLElement>(
					".ant-menu-item-selected"
				);
			selectedItem?.scrollIntoView({ block: "nearest" });
		});

		return () => cancelAnimationFrame(frameID);
	}, [activeReportKey, reports]);

	const handleGroupChange = (groupKey: string) => {
		const group = analyticsGroups.find((item) => item.key === groupKey);
		const firstReportKey = group?.reports[0]?.key;
		if (!firstReportKey) {
			return;
		}
		if (group?.reports.some((report) => report.key === activeReportKey)) {
			return;
		}
		onReportChange(firstReportKey);
	};

	return (
		<Flex vertical flex={1} className={subEntityStyles.container}>
			<Tabs
				activeKey={activeGroup?.key}
				onChange={handleGroupChange}
				items={groupItems}
				className={styles.mainGroups}
			/>
			<Flex flex={1} className={subEntityStyles.mainContent}>
				<div className={subEntityStyles.leftNav}>
					<Flex vertical className={styles.leftNavInner}>
						<Flex vertical className={styles.sidebarHeader}>
							<Typography.Text strong>
								{activeGroup?.label ?? "Reports"}
							</Typography.Text>
						</Flex>
						<div ref={menuWrapRef} className={styles.menuWrap}>
							<Menu
								mode="vertical"
								selectedKeys={
									activeReportKey ? [activeReportKey] : []
								}
								items={menuItems}
							/>
						</div>
					</Flex>
				</div>
				<Flex flex={1} vertical className={styles.reportContent}>
					{children}
				</Flex>
			</Flex>
		</Flex>
	);
};
